import React from "react";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Unstable_Grid2"; // Grid version 2
import GitHubIcon from "@mui/icons-material/GitHub";
import LaunchIcon from "@mui/icons-material/Launch";
import "animate.css/animate.min.css";
import { AnimationOnScroll } from "react-animation-on-scroll";
import Project1Img from "../assets/images/Project1_Img1.png";
import Project2Img from "../assets/images/Project2_img.png";
import Project3Img from "../assets/images/SolarPic.jpg";

const projects = [
  {
    name: "Heat Stress Metrics for US Census Tracts 1998-2020",
    description:
      "Extreme heat exposure is a growing public health threat. Heat-health research has commonly used dry-bulb temperature to characterize heat exposure, partly due to limited availability of spatially explicit, public-health-aligned datasets that integrate multiple meteorological factors to quantify heat stress. We address this gap by providing hourly Heat Index (HI), Wet-Bulb Globe Temperature (WBGT), and Universal Thermal Climate Index (UTCI) for U.S. census tract boundaries across the contiguous United States from 1998–2020. Heat-stress fields were generated by integrating PRISM, ERA5-Land, and National Solar Radiation Database (NSRDB) products, with near-surface temperature and moisture fields reconstructed and ancillary variables interpolated to a harmonized 800-m grid. Heat-stress indices were computed using validated physical models and aggregated to census tracts using area- and population-weighted methods. Validation against station networks shows stable performance for sample year 2010 May–September, with air-temperature root mean squared error (RMSE) of 1.70 °C, Heat Index RMSE of 3.20 °C, WBGT RMSE of 2.90 °C, and UTCI RMSE of 3.26 °C. These tract-level hourly heat-stress datasets enable direct linkage with public health data.",
    github: "https://github.com",
    link: "https://halle-lab.com/project-page",
    technologies: [],
    image: Project1Img,
  },
  {
    name: "An Intersectional Analysis of Climate Risk and Susceptibility among Urban Schools Across 20 Major U.S. Cities",
    description:
      "U.S. schools face growing climate-related threats, including extreme heat and air pollution, which harm student health and learning. These risks intersect with socioeconomic and built environmental conditions that shape school-level susceptibility. We analyzed 4,754 public schools across 20 major U.S. cities using climate risk (PM2.5, ozone, extreme heat days >102°F) and susceptibility indicators (poverty, impervious cover, inverse greenspace). Spatial lag regression and HDBSCAN clustering identified city-specific risk–susceptibility relationships, with significant positive associations in Los Angeles, Philadelphia, Phoenix, New York, and Riverside, and 566 schools exhibiting both high risk and high susceptibility. Climate vulnerability is spatially uneven across urban schools, underscoring the need for targeted interventions such as greening and HVAC upgrades to protect student wellbeing.",
    github: "",
    link: "https://halle-lab.com/project-page2",
    technologies: [],
    image: Project2Img,
  },
  {
    name: "Heat and Energy Intelligence for Schools",
    description:
      "This goal of this project is to develop a heat and energy site intelligence model to support public infrastructure investment. The aim is to translate physical site conditions—such as solar exposure, surface materials, and building orientation—into clear, decision-ready information that can inform capital planning before design and construction begin. The current phase focuses on pilot preschool campuses. We are using drone mapping to better understand how outdoor heat and building energy use intersect. The broader aim is to explore how standardized site-level evidence can strengthen funding decisions and improve long-term infrastructure resilience.",
    github: "https://github.com",
    link: "https://halle-lab.com/project-page3",
    technologies: [],
    image: Project3Img,
  },
];

const Projects = () => {
  const windowWidth: Number = window.innerWidth;
  return (
    <>
      <div id="Projects" className="bg-zinc-800 mb-8 px-12 md:mt-16 ">
        <Typography
          variant="h6"
          noWrap
          component="h6"
          sx={{
            color: "white",
            textDecoration: "none",
            fontSize: "5vh",
            display: "flex",
            justifyContent: "center",
            marginBottom: "3vh",
            fontWeight: "300",
          }}
        >
          Current Projects
        </Typography>
        <Grid container spacing={4} className="justify-center">
          {projects.map((project, index) =>
            // odd-numbered projects should have image on left, and even on the right
            // when width is less than 900 they just stack on top of each other, so we don't want the left-right functionality below that width
            index % 2 === 0 || windowWidth < 900 ? (
              <React.Fragment key={index}>
                <Grid sm={12} md={6}>
                  <AnimationOnScroll animateIn="animate__fadeIn" animateOnce>
                    <a target="_blank" href={project.link} rel="noreferrer">
                      <img src={project.image} alt="project logo" />
                    </a>
                  </AnimationOnScroll>
                </Grid>

                <Grid sm={12} md={6}>
                  <AnimationOnScroll animateIn="animate__fadeIn" animateOnce>
                    <a href={project.link} target="_blank" rel="noreferrer">
                      <h6 className="flex justify-end mb-4 text-2xl">
                        {project.name}
                      </h6>
                    </a>
                    <Paper className="p-2 mb-4" elevation={3}>
                      <p className="text-md">{project.description}</p>
                    </Paper>
                    <div className="flex justify-end flex-wrap gap-x-4 gap-y-1">
                      {project.technologies.map((tech, index) => (
                        <span key={index} className="text-md text-gray-400">
                          {tech}
                        </span>
                      ))}
                    </div>
                    <div className="flex justify-end mt-3 gap-3">
                      <a href={project.link} target="_blank" rel="noreferrer">
                        <LaunchIcon></LaunchIcon>
                      </a>
                    </div>
                  </AnimationOnScroll>
                </Grid>
              </React.Fragment>
            ) : (
              <React.Fragment key={index}>
                <Grid sm={12} md={6}>
                  <AnimationOnScroll animateIn="animate__fadeIn" animateOnce>
                    <a href={project.link} target="_blank" rel="noreferrer">
                      <h6 className="flex mb-4 text-2xl">{project.name}</h6>
                    </a>
                    <Paper className="p-2 mb-4" elevation={3}>
                      <p className="text-md">{project.description}</p>
                    </Paper>
                    <div className="flex justify-end flex-wrap gap-x-4 gap-y-1">
                      {project.technologies.map((tech, index) => (
                        <span key={index} className="text-md text-gray-400">
                          {tech}
                        </span>
                      ))}
                    </div>
                    <div className="flex justify-end mt-3 gap-3">
                      <a href={project.link} target="_blank" rel="noreferrer">
                        <LaunchIcon></LaunchIcon>
                      </a>
                    </div>
                  </AnimationOnScroll>
                </Grid>
                <Grid sm={12} md={6}>
                  <AnimationOnScroll animateIn="animate__fadeIn" animateOnce>
                    <a target="_blank" href={project.link} rel="noreferrer">
                      <img src={project.image} alt="project logo" />
                    </a>
                  </AnimationOnScroll>
                </Grid>
              </React.Fragment>
            )
          )}
        </Grid>
      </div>
    </>
  );
};

export default Projects;
